import { Wrench, ShieldCheck, Network, Disc, Settings, Headphones, Clock, Award } from 'lucide-react';

const SERVICES_DATA = [
  {
    id: 'repair',
    title: 'Computer Repair',
    description: 'Expert laptop and desktop repair services with quick turnaround time.',
    icon: Wrench,
  },
  {
    id: 'amc',
    title: 'AMC Plans',
    description: 'Annual maintenance contracts for hassle-free IT infrastructure management.',
    icon: ShieldCheck,
  },
  {
    id: 'networking',
    title: 'Networking',
    description: 'Complete networking solutions including LAN setup and configuration.',
    icon: Network,
  },
  {
    id: 'software',
    title: 'Software Installation',
    description: 'Licensed software installation and system optimization services.',
    icon: Disc,
  },
];

const FEATURES = [
  { icon: Settings, label: 'Expert Technicians' },
  { icon: Headphones, label: '24/7 Support' },
  { icon: Clock, label: 'Quick Service' },
  { icon: Award, label: 'Quality Guaranteed' },
];

export const ServicesSection = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header - Right aligned */}
        <div className="mb-16 text-right opacity-0 animate-fade-in-up">
          <span className="text-sm font-medium tracking-widest uppercase text-orange-500 mb-4 block">
            How We Help
          </span>
          <h2
            data-testid="services-title"
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-4"
          >
            Our Services
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl ml-auto">
            Comprehensive IT services to keep your business running smoothly.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {SERVICES_DATA.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="group bg-white border border-slate-200 p-8 card-hover rounded-sm relative overflow-hidden opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 100 + 200}ms` }}
              >
                {/* Hover accent */}
                <div className="absolute top-0 left-0 w-1 h-0 bg-orange-500 group-hover:h-full transition-all duration-500"></div>

                {/* Icon */}
                <div className="w-14 h-14 bg-slate-100 rounded-sm flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300">
                  <IconComponent className="h-7 w-7 text-slate-700 group-hover:text-white transition-colors duration-300" />
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-slate-900 mb-3 uppercase tracking-tight">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>

                {/* Number indicator */}
                <div className="absolute bottom-4 right-4 text-6xl font-bold text-slate-100 group-hover:text-orange-100 transition-colors">
                  0{index + 1}
                </div>
              </div>
            );
          })}
        </div>

        {/* Features Bar */}
        <div
          data-testid="features-bar"
          className="bg-slate-900 rounded-sm p-8 md:p-12 opacity-0 animate-fade-in-up"
          style={{ animationDelay: '600ms' }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {FEATURES.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="flex flex-col items-center text-center"
                >
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mb-4">
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-white font-medium uppercase tracking-wider text-sm">
                    {feature.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
